import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Users, Calendar as CalIcon, Clock, Sparkles, BookmarkPlus, Trash2, Loader2, ChevronDown } from "lucide-react";

function splitSkills(s) {
    return String(s || "").split(",").map((x) => x.trim()).filter(Boolean);
}

export default function BulkInterviewAssignDialog({ open, candidateIds, onClose, onDone }) {
    const [interviewers, setInterviewers] = useState([]);
    const [interviewerId, setInterviewerId] = useState("");
    const [roundName, setRoundName] = useState("Technical Round 1");
    const [mode, setMode] = useState("Video");
    const [onlineOffline, setOnlineOffline] = useState("online");
    const [duration, setDuration] = useState(45);
    const [date, setDate] = useState("");
    const [time, setTime] = useState("");
    const [withTime, setWithTime] = useState(false);
    const [saving, setSaving] = useState(false);
    const [location, setLocation] = useState("");
    const [notes, setNotes] = useState("");

    // Skills (persist on save-as-template; auto-push to candidates when scheduling)
    const [hardSkills, setHardSkills] = useState("");
    const [softSkills, setSoftSkills] = useState("");
    const [critical, setCritical] = useState("");

    // Round templates (company-wide)
    const [templates, setTemplates] = useState([]);
    const [tplOpen, setTplOpen] = useState(false);
    const [tplLoading, setTplLoading] = useState(false);
    const [showSaveTpl, setShowSaveTpl] = useState(false);
    const [tplName, setTplName] = useState("");
    const [tplSaving, setTplSaving] = useState(false);
    const [appliedTplId, setAppliedTplId] = useState(null);
    const [pendingDeleteTpl, setPendingDeleteTpl] = useState(null);

    const loadTemplates = async () => {
        setTplLoading(true);
        try { const { data } = await api.get("/round-templates"); setTemplates(data || []); }
        catch (err) { toast.error(formatApiError(err)); }
        finally { setTplLoading(false); }
    };

    useEffect(() => {
        if (!open) return;
        (async () => {
            try {
                const { data } = await api.get("/company/interviewers");
                setInterviewers(data || []);
            } catch (err) { toast.error(formatApiError(err)); }
        })();
        loadTemplates();
    }, [open]);

    if (!open) return null;

    const applyTemplate = (t) => {
        setRoundName(t.round_name || "");
        setMode(t.mode || "Video");
        setOnlineOffline(t.online_offline || "online");
        setDuration(t.duration_minutes || 30);
        setLocation(t.location || "");
        setNotes(t.notes || "");
        setHardSkills((t.hard_skills || []).join(", "));
        setSoftSkills((t.soft_skills || []).join(", "));
        setCritical((t.critical || []).join(", "));
        setAppliedTplId(t.id);
        setTplOpen(false);
        toast.success(`Applied template "${t.name}"`);
    };

    const deleteTemplate = (t) => {
        // Open the shadcn AlertDialog instead of the native browser confirm.
        setPendingDeleteTpl(t);
    };

    const confirmDeleteTemplate = async () => {
        const t = pendingDeleteTpl;
        if (!t) return;
        try {
            await api.delete(`/round-templates/${t.id}`);
            toast.success("Template deleted");
            setPendingDeleteTpl(null);
            loadTemplates();
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const saveAsTemplate = async () => {
        if (!tplName.trim()) { toast.error("Template name is required"); return; }
        if (!roundName.trim()) { toast.error("Round name is required"); return; }
        setTplSaving(true);
        try {
            const body = {
                name: tplName.trim(),
                round_name: roundName.trim(),
                mode,
                online_offline: onlineOffline,
                duration_minutes: Number(duration) || 30,
                location: location || null,
                notes: notes || null,
                hard_skills: splitSkills(hardSkills),
                soft_skills: splitSkills(softSkills),
                critical: splitSkills(critical),
            };
            const { data } = await api.post("/round-templates", body);
            toast.success(`Template "${data.name}" saved`);
            setShowSaveTpl(false);
            setTplName("");
            loadTemplates();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setTplSaving(false); }
    };

    const submit = async () => {
        if (!candidateIds.length) { toast.error("Select at least one candidate"); return; }
        if (!interviewerId) { toast.error("Pick an interviewer"); return; }
        if (!roundName.trim()) { toast.error("Round name is required"); return; }
        setSaving(true);
        try {
            const body = {
                candidate_ids: candidateIds,
                interviewer_id: interviewerId,
                round_name: roundName.trim(),
                mode,
                online_offline: onlineOffline,
                duration_minutes: Number(duration) || 30,
                location: location || null,
                notes: notes || null,
            };
            if (date) {
                if (withTime && time) {
                    body.scheduled_at = new Date(`${date}T${time}:00`).toISOString();
                } else {
                    body.scheduled_date = date;
                }
            }
            const { data } = await api.post("/candidates/bulk-schedule-interviews", body);
            // If the template pushed skills, also push onto newly-created interviews.
            const hs = splitSkills(hardSkills), ss = splitSkills(softSkills), cr = splitSkills(critical);
            if (hs.length || ss.length || cr.length) {
                try {
                    await api.post("/candidates/bulk-push-skills", {
                        candidate_ids: candidateIds,
                        hard_skills: hs,
                        soft_skills: ss,
                        critical: cr,
                    });
                } catch { /* non-fatal: scheduling already succeeded */ }
            }
            toast.success(`Scheduled ${data.created} interview${data.created === 1 ? "" : "s"}${data.skipped ? ` · ${data.skipped} skipped` : ""}`);
            onDone?.();
            onClose?.();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
            <DialogContent className="max-w-2xl rounded-none border border-border max-h-[92vh] overflow-y-auto" data-testid="bulk-interview-assign-dialog">
                <DialogTitle className="font-display flex items-center gap-2">
                    <Users className="w-5 h-5 text-ikb" /> Bulk assign interviewer
                </DialogTitle>
                <DialogDescription>
                    Create interview rounds for {candidateIds.length} selected candidate{candidateIds.length === 1 ? "" : "s"}. Use a saved template for one-click setup.
                </DialogDescription>

                {/* ── Templates row ─────────────────────────────── */}
                <div className="mt-2 border border-border bg-secondary/40 p-3" data-testid="round-templates-row">
                    <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                            <button
                                type="button"
                                onClick={() => setTplOpen((v) => !v)}
                                className="text-[11px] font-mono uppercase tracking-widest border border-border bg-white px-2.5 py-1.5 hover:border-ikb hover:text-ikb inline-flex items-center gap-1.5"
                                data-testid="round-templates-dropdown-btn"
                            >
                                <Sparkles className="w-3 h-3" /> Use a template
                                <ChevronDown className="w-3 h-3" />
                            </button>
                            {appliedTplId && (
                                <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-700 bg-emerald-50 border border-emerald-300 px-2 py-0.5" data-testid="round-template-applied-badge">
                                    Template applied
                                </span>
                            )}
                        </div>
                        <button
                            type="button"
                            onClick={() => { setShowSaveTpl((v) => !v); setTplName(roundName || ""); }}
                            className="text-[11px] font-mono uppercase tracking-widest border border-border bg-white px-2.5 py-1.5 hover:border-ikb hover:text-ikb inline-flex items-center gap-1.5"
                            data-testid="round-template-save-btn"
                        >
                            <BookmarkPlus className="w-3 h-3" /> Save as template
                        </button>
                    </div>
                    {tplOpen && (
                        <div className="mt-2 border border-border bg-white max-h-56 overflow-y-auto" data-testid="round-templates-list">
                            {tplLoading ? (
                                <div className="p-3 text-xs font-mono text-muted-foreground inline-flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Loading…</div>
                            ) : templates.length === 0 ? (
                                <div className="p-3 text-xs font-mono text-muted-foreground">No templates yet. Set up this round and click "Save as template".</div>
                            ) : templates.map((t) => (
                                <div key={t.id} className="flex items-center justify-between p-2.5 border-b border-border last:border-b-0 hover:bg-secondary/40" data-testid={`round-template-row-${t.id}`}>
                                    <button type="button" onClick={() => applyTemplate(t)} className="text-left flex-1 min-w-0" data-testid={`round-template-apply-${t.id}`}>
                                        <div className="text-sm font-semibold truncate">{t.name}</div>
                                        <div className="text-[10px] font-mono text-muted-foreground mt-0.5">
                                            {t.round_name} · {t.mode} · {t.duration_minutes}min · {t.online_offline}
                                            {(t.hard_skills?.length || t.soft_skills?.length || t.critical?.length) ? ` · ${(t.hard_skills?.length || 0) + (t.soft_skills?.length || 0) + (t.critical?.length || 0)} skills` : ""}
                                        </div>
                                    </button>
                                    <button type="button" onClick={() => deleteTemplate(t)} className="ml-2 h-8 w-8 inline-flex items-center justify-center text-rose-700 hover:bg-rose-50 border border-transparent hover:border-rose-200" data-testid={`round-template-delete-${t.id}`}>
                                        <Trash2 className="w-3.5 h-3.5" />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    {showSaveTpl && (
                        <div className="mt-2 border border-border bg-white p-3 flex items-end gap-2 flex-wrap" data-testid="round-template-save-form">
                            <label className="block text-xs flex-1 min-w-[180px]">
                                <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">Template name</span>
                                <Input value={tplName} onChange={(e) => setTplName(e.target.value)} placeholder="e.g. Backend L5 — Tech Round 1" className="rounded-none mt-1" data-testid="round-template-name-input" />
                            </label>
                            <Button onClick={saveAsTemplate} disabled={tplSaving} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="round-template-save-submit">
                                {tplSaving ? "Saving…" : "Save template"}
                            </Button>
                            <Button variant="outline" onClick={() => setShowSaveTpl(false)} className="rounded-none">Cancel</Button>
                        </div>
                    )}
                </div>

                <div className="space-y-4 mt-3">
                    <div>
                        <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/70 mb-1">Interviewer</div>
                        <select
                            value={interviewerId}
                            onChange={(e) => setInterviewerId(e.target.value)}
                            className="w-full h-10 px-2 border border-border bg-white text-sm"
                            data-testid="bulk-iv-interviewer"
                        >
                            <option value="">Select an interviewer…</option>
                            {interviewers.map((iv) => {
                                const roleLabel = iv.role && iv.role !== "interviewer"
                                    ? ` — ${iv.role.charAt(0).toUpperCase()}${iv.role.slice(1)}`
                                    : "";
                                const emailLabel = iv.email ? ` · ${iv.email}` : "";
                                return (
                                    <option key={iv.id} value={iv.id}>
                                        {(iv.name || iv.email) + roleLabel + emailLabel}
                                    </option>
                                );
                            })}
                        </select>
                        <div className="mt-1 text-[10px] font-mono text-muted-foreground">
                            Any teammate — including recruiters and admins — can be assigned to interview a candidate.
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <label className="block text-xs">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">Round name</span>
                            <Input value={roundName} onChange={(e) => setRoundName(e.target.value)} placeholder="e.g. Tech Round 1" className="rounded-none mt-1" data-testid="bulk-iv-round" />
                        </label>
                        <label className="block text-xs">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">Duration (min)</span>
                            <Input type="number" min={5} step={5} value={duration} onChange={(e) => setDuration(e.target.value)} className="rounded-none mt-1" data-testid="bulk-iv-duration" />
                        </label>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div className="block text-xs">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">Mode</span>
                            <select value={mode} onChange={(e) => setMode(e.target.value)} className="w-full h-10 px-2 border border-border bg-white text-sm mt-1" data-testid="bulk-iv-mode">
                                <option value="Phone">Phone</option>
                                <option value="Video">Video</option>
                                <option value="Onsite">Onsite</option>
                                <option value="Take-home">Take-home</option>
                            </select>
                        </div>
                        <div className="block text-xs">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">Online / Offline</span>
                            <div className="inline-flex border border-border bg-white mt-1 w-full">
                                <button type="button" onClick={() => setOnlineOffline("online")} className={`flex-1 px-3 py-2 text-xs font-mono uppercase tracking-widest ${onlineOffline === "online" ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid="bulk-iv-online">Online</button>
                                <button type="button" onClick={() => setOnlineOffline("offline")} className={`flex-1 px-3 py-2 text-xs font-mono uppercase tracking-widest border-l border-border ${onlineOffline === "offline" ? "bg-ikb text-white" : "hover:bg-secondary"}`} data-testid="bulk-iv-offline">Offline</button>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 gap-3">
                        <label className="block text-xs">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">Rating skills (optional) — auto-pushed to each interview</span>
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-1">
                                <Input value={hardSkills} onChange={(e) => setHardSkills(e.target.value)} placeholder="Hard skills (comma-separated)" className="rounded-none" data-testid="bulk-iv-hard-skills" />
                                <Input value={softSkills} onChange={(e) => setSoftSkills(e.target.value)} placeholder="Soft skills" className="rounded-none" data-testid="bulk-iv-soft-skills" />
                                <Input value={critical} onChange={(e) => setCritical(e.target.value)} placeholder="Critical" className="rounded-none" data-testid="bulk-iv-critical-skills" />
                            </div>
                        </label>
                    </div>

                    <div>
                        <div className="flex items-center justify-between mb-1">
                            <span className="text-[10px] font-mono uppercase tracking-widest text-foreground/70">Date</span>
                            <label className="text-[10px] font-mono flex items-center gap-1.5 cursor-pointer">
                                <input type="checkbox" checked={withTime} onChange={(e) => setWithTime(e.target.checked)} data-testid="bulk-iv-with-time" />
                                Also set exact time
                            </label>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="rounded-none" data-testid="bulk-iv-date" />
                            {withTime && (
                                <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} className="rounded-none" data-testid="bulk-iv-time" />
                            )}
                        </div>
                        {!withTime && date && (
                            <div className="mt-1.5 inline-flex items-center gap-1.5 text-[10px] font-mono text-foreground/60">
                                <Clock className="w-3 h-3" /> Date-only (campus mode) — each interview inherits this date; interviewer can pick slots on the day
                            </div>
                        )}
                    </div>

                    <div className="border border-border bg-secondary/40 px-4 py-3 flex items-center justify-between" data-testid="bulk-iv-preview">
                        <div>
                            <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Will schedule</div>
                            <div className="font-display font-black text-3xl text-ikb mt-0.5">{candidateIds.length} <span className="text-base font-normal text-foreground/60">interview{candidateIds.length === 1 ? "" : "s"}</span></div>
                        </div>
                        <CalIcon className="w-8 h-8 text-ikb/40" />
                    </div>
                </div>

                <div className="flex justify-end gap-2 mt-4">
                    <Button variant="outline" onClick={onClose} className="rounded-none" data-testid="bulk-iv-cancel">Cancel</Button>
                    <Button onClick={submit} disabled={saving || !interviewerId || !candidateIds.length} className="rounded-none bg-ikb hover:bg-[#001f7a] text-white" data-testid="bulk-iv-create">
                        <Sparkles className="w-4 h-4 mr-1" /> {saving ? "Scheduling…" : "Schedule interviews"}
                    </Button>
                </div>
            </DialogContent>

            <AlertDialog open={!!pendingDeleteTpl} onOpenChange={(o) => !o && setPendingDeleteTpl(null)}>
                <AlertDialogContent className="rounded-none" data-testid="round-template-delete-confirm">
                    <AlertDialogHeader>
                        <AlertDialogTitle>Delete this template?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Delete template "{pendingDeleteTpl?.name}"? This cannot be undone — anyone on your team using it will have to rebuild it from scratch.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel className="rounded-none" data-testid="round-template-delete-cancel">Cancel</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDeleteTemplate}
                            className="rounded-none bg-rose-600 hover:bg-rose-700 text-white"
                            data-testid="round-template-delete-confirm-btn"
                        >
                            Delete template
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Dialog>
    );
}
